import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "gold";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: "sm" | "md" | "lg";
  icon?: ReactNode;
  block?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-cap-red text-white border-cap-red hover:bg-red-700 shadow-hero",
  secondary:
    "bg-cap-blue text-white border-cap-blue hover:bg-blue-800 shadow-glow-blue",
  ghost:
    "bg-transparent text-white border-white/30 hover:bg-white/10 hover:border-white",
  danger:
    "bg-red-700 text-white border-red-700 hover:bg-red-800",
  gold:
    "bg-cap-gold text-cap-deep border-cap-gold hover:bg-yellow-400 shadow-hero",
};

const SIZES = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-5 py-2.5 text-sm",
  lg: "px-7 py-3.5 text-base",
} as const;

export function Button({
  variant = "primary",
  size = "md",
  icon,
  block,
  className,
  children,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      className={[
        "inline-flex items-center justify-center gap-2 rounded-full border-2 font-display tracking-wider uppercase",
        "transition-all duration-150 transform active:scale-95",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100",
        "focus:outline-none focus:ring-2 focus:ring-cap-gold focus:ring-offset-2 focus:ring-offset-cap-deep",
        VARIANTS[variant],
        SIZES[size],
        block ? "w-full" : "",
        className ?? "",
      ].join(" ")}
    >
      {icon ? <span className="text-base leading-none">{icon}</span> : null}
      {children}
    </button>
  );
}
